import { clearAuthSession, getAuthToken } from './authSession';
export interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  template_type: string;
  owner_type: string;
  config: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
}

export interface GeneratedReport {
  id: string;
  template_id?: string;
  title: string;
  category: string;
  format: string;
  status: string;
  hotel_id?: string;
  generated_by: string;
  generated_by_role: string;
  period_start?: string;
  period_end?: string;
  file_size_bytes: number;
  error_message?: string;
  approved_by?: string;
  approved_at?: string;
  archived: boolean;
  created_at: string;
  completed_at?: string;
}

export interface GenerateReportRequest {
  template_id?: string;
  title: string;
  category: string;
  format: string;
  period_start?: string;
  period_end?: string;
  parameters?: Record<string, unknown>;
}



const base = `${import.meta.env.VITE_API_BASE_URL || ''}/api/v1/reports`;

async function request<T>(path: string, consume: (response: Response) => Promise<T>, body?: GenerateReportRequest): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 60000);
  try {
    const response = await fetch(`${base}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: { Authorization: `Bearer ${getAuthToken()}`, ...(body ? {'Content-Type': 'application/json'} : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
      signal: controller.signal,
    });
    if (response.status === 401) {
      clearAuthSession();
      throw new Error('Session expirée. Reconnectez-vous pour accéder aux rapports.');
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(typeof error.message === 'string' ? error.message : 'Le rapport est indisponible. Réessayez.');
    }
    // Keep cancellation active until the response body has been received too.
    return await consume(response);
  } catch (error) {
    if (controller.signal.aborted) throw new Error('Le serveur ne répond pas. Actualisez l’historique avant de relancer la génération.');
    throw error;
  } finally {
    window.clearTimeout(timer);
  }
}

async function list<T>(path: string): Promise<T[]> {
  const json = await request(path, response => response.json());
  if (!Array.isArray(json.data)) throw new Error('Réponse de rapports invalide. Réessayez.');
  return json.data;
}

export const reportService = {
  async previewReport(id: string): Promise<Blob> {
    return request(`/${encodeURIComponent(id)}/download?preview=1`, async response => {
      const blob = await response.blob();
      if (!blob.size || !/^(application\/pdf|text\/html)/i.test(blob.type)) throw new Error('Aperçu du rapport invalide.');
      return blob;
    });
  },
  getTemplates: (_category?: string) => list<ReportTemplate>('/templates?category=hotel'),
  getReports: () => list<GeneratedReport>('?category=hotel'),
  async generateReport(body: GenerateReportRequest): Promise<GeneratedReport> {
    const json = await request('/generate', response => response.json(), body);
    if (!json.data?.id) throw new Error('Confirmation de génération invalide. Actualisez l’historique.');
    if (json.data.status === 'failed') throw new Error(json.data.error_message || 'La génération du rapport a échoué.');
    return json.data;
  },
  async downloadReport(id: string): Promise<void> {
    const {blob, disposition} = await request(`/${encodeURIComponent(id)}/download`, async response => ({
      blob: await response.blob(),
      disposition: response.headers.get('content-disposition') || '',
    }));
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    const plain = disposition.match(/filename="([^"]+)"|filename=([^;]+)/i);
    let filename = 'rapport';
    try { filename = encoded ? decodeURIComponent(encoded[1]) : plain ? (plain[1] || plain[2]).trim() : filename; } catch { /* Safe fallback. */ }
    filename = filename.replace(/[\\/\x00-\x1f]/g, '_');
    if (!blob.size || /json|html/i.test(blob.type)) throw new Error('Le fichier du rapport est invalide.');
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};
